/*  MUI  */
import { Typography } from '@mui/material';

/*  Shared  */
import {
    ManagedTableV2UI,
    useManagedTableV2Context
} from 'application/Shared/Components/Table';
import { releaseHeader } from 'application/Shared/Components/Table/Headers';
import { Route } from 'application/Shared/Helpers';
import { Confirm } from 'application/Shared/Modals';

/*  NPM  */
import compareVersions from 'compare-versions';

/* Hooks */
import {
    useDeleteDevice,
    useFetchDevice
} from 'hooks/dataAccess/Device/device';

/*  React  */
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

/* Internal */
import { useDeviceTableContext } from './DeviceTableContext';

const csvFields = (t) => [
    { key: 'name', label: t('Common.name') },
    { key: 'macAddress', label: t('Common.macAddress') },
    { key: 'os', label: t('Common.os') },
    { key: 'release', label: t('Common.version') },
    { key: 'dateUpdated', label: t('Common.lastUpdated') }
];

const processReleaseFilter = ({
    filters,
    releaseFilterOptions,
    rows,
    setFilters
}) => {
    if (!rows?.length) {
        return;
    }

    const releases = [];

    rows.forEach((r) => {
        const { release } = r;
        if (!releases.includes(release)) {
            releases.push(release);
        }
    });

    const sortedASC = releases.sort(compareVersions);
    const sortedDESC = sortedASC.reverse();
    const updatedReleaseHeader = releaseHeader(sortedDESC);

    if (
        updatedReleaseHeader?.options?.length > 1 &&
        updatedReleaseHeader?.options?.length !== releaseFilterOptions?.length
    ) {
        setFilters({
            ...filters,
            release: { ...updatedReleaseHeader }
        });
    }
};

const deleteFn = async ({
    clientId,
    deleteDevice,
    deviceId,
    setDeletingId,
    setRerender
}) => {
    const success = await deleteDevice({ clientId, deviceId });
    setDeletingId(null);
    if (success) {
        setRerender(true);
    }
};

const DeleteConfirmationModal = ({
    clientId,
    deletingId,
    setDeletingId,
    setRerender
}) => {
    const { t } = useTranslation();
    const { deleteDevice } = useDeleteDevice(t);

    return (
        <Confirm
            modalOpen
            promptConfirmFn={() =>
                deleteFn({
                    clientId,
                    deleteDevice,
                    deviceId: deletingId,
                    setDeletingId,
                    setRerender
                })
            }
            promptCancelFn={() => {
                setDeletingId(null);
            }}
            promptTitle={t('Devices.confirm_delete')}
            promptText={
                <Typography
                    variant="span"
                    dangerouslySetInnerHTML={{
                        __html: t('Devices.delete_notice')
                    }}
                />
            }
        />
    );
};

export const DeviceTable = () => {
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();
    const { deletingId, setDeletingId } = useDeviceTableContext();
    const { filters, rowData, setFilters, setRerender } =
        useManagedTableV2Context();

    const [reloadDevices, setReloadDevices] = useState(false);
    const { devices, refetchDevices } = useFetchDevice({ clientId });

    const tableCsvFields = useMemo(() => csvFields(t), [t]);

    const releaseFilterOptions = filters.release?.options;

    const baseRoute = Route.buildBaseAdminRoute({ clientId, partnerId });

    useEffect(() => {
        if (devices) {
            processReleaseFilter({
                filters,
                releaseFilterOptions,
                rows: devices,
                setFilters
            });
        }
    }, [devices, filters, releaseFilterOptions, setFilters]);

    useEffect(() => {
        setReloadDevices(true);
    }, [rowData]);

    // This table is set to autoupdate every 30 seconds. That means, that if we have a filter on with a certain version/release,
    // the next time it autoupdates, the parameters included in the API call will only include that particular version, i.e.,
    // params: { releases: 1.85.0, otherParams}. This will affect the versions filter because the rows that come back from the api call
    // will be only the ones with version 1.85.0, and therefore, the filter will be set up to have only that version as an option
    // and will "forget" it had a variety of other versions before.
    // This is why there is a secondary call to fetch the devices via useFetchDevice. This will be independent of what filters
    // are selected, and therefore, will be unfiltered. Issue is, we are making two calls to the device endpoint.
    useEffect(() => {
        if (reloadDevices) {
            refetchDevices();
            setReloadDevices(false);
        }
    }, [reloadDevices, refetchDevices]);

    return (
        <>
            <ManagedTableV2UI
                csvExport
                csvExportFields={tableCsvFields}
                csvExportFileName="devices"
                renderFooter
                titleDef={{
                    subtitle: (
                        <Trans
                            i18nKey="UsersAndDevices.deploymentHelpWithLink"
                            components={{
                                Link: (
                                    <Link
                                        className="link underline"
                                        to={`${baseRoute}/identityManagement/deploymentScripts`}
                                        style={{ fontWeight: 600 }}
                                        data-trackid="admin_users_deviceTable_deploymentScripts_routeTo"
                                    >
                                        {t('Common.deployment_scripts')}
                                    </Link>
                                )
                            }}
                        />
                    ),
                    titleKey: 'Common.devicesWithCount'
                }}
            />
            {deletingId && (
                <DeleteConfirmationModal
                    clientId={clientId}
                    deletingId={deletingId}
                    setDeletingId={setDeletingId}
                    setRerender={setRerender}
                />
            )}
        </>
    );
};
